import { notifyError, notifySuccess, notifyWarning } from '@/store/useToastStore';
import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Plus, CreditCard, RotateCcw, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { database, txn } from '@/db';
import { Product, PurchaseOrder, Supplier } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { formatCurrency } from '@/utils/format';
import { createPurchaseReturn, recordSupplierPayment, type PurchaseLine } from '@/inventory/PurchaseService';
import { logActivity } from '@/utils/auditLog';

type DialogMode = 'supplier' | 'payment' | 'return' | null;

export function SupplierListScreen() {
  const navigate = useNavigate();
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const userId = useAppStore((s) => s.currentUser?.id);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Supplier | null>(null);
  const [mode, setMode] = useState<DialogMode>(null);

  const suppliers = useObservable<Supplier[]>(
    () => {
      if (!merchantId) return null;
      const conditions: any[] = [Q.where('merchant_id', merchantId), Q.where('is_deleted', false)];
      if (search.trim()) {
        const q = `%${search.trim().toLowerCase()}%`;
        conditions.push(Q.or(Q.where('name', Q.like(q)), Q.where('phone', Q.like(q)), Q.where('gstin', Q.like(q))));
      }
      return database.get<Supplier>('suppliers').query(...conditions, Q.sortBy('updated_at', Q.desc)).observe();
    },
    [merchantId, search],
    [],
  );
  const purchaseOrders = useObservable<PurchaseOrder[]>(
    () => activeStoreId
      ? database.get<PurchaseOrder>('purchase_orders').query(
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
        Q.sortBy('created_at', Q.desc),
        Q.take(50),
      ).observe()
      : null,
    [activeStoreId],
    [],
  );
  const supplierNames = useMemo(() => new Map(suppliers.map((s) => [s.id, s.name])), [suppliers]);

  if (!merchantId || !activeStoreId || !userId) return <div className="text-body text-fg-secondary">No active store. Sign in again.</div>;

  return (
    <div className="grid h-full grid-cols-1 gap-6 overflow-auto lg:grid-cols-[1fr_420px] lg:overflow-visible">
      <section className="flex min-h-0 flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-display text-fg-primary">Suppliers</h1>
            <p className="mt-1 text-body text-fg-secondary">{suppliers.length} suppliers</p>
          </div>
          <Button onClick={() => { setSelected(null); setMode('supplier'); }}><Plus size={16} /> New supplier</Button>
        </div>
        <Input placeholder="Search supplier, phone, GSTIN" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" />
        <div className="flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
          {suppliers.map((supplier) => (
            <div key={supplier.id} className="flex items-center justify-between border-b border-stroke-hairline/5 px-4 py-3">
              <button type="button" className="min-w-0 text-left" onClick={() => { setSelected(supplier); setMode('supplier'); }}>
                <div className="font-medium text-fg-primary">{supplier.name}</div>
                <div className="text-label text-fg-tertiary">{[supplier.contactPerson, supplier.phone, supplier.gstin].filter(Boolean).join(' · ') || 'No contact details'}</div>
              </button>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-warning/10 px-2 py-0.5 text-label text-warning-ink tabular-nums">{formatCurrency(supplier.currentBalance)}</span>
                <Button variant="secondary" size="sm" onClick={() => { setSelected(supplier); setMode('payment'); }}><CreditCard size={13} /> Pay</Button>
                <Button variant="secondary" size="sm" onClick={() => { setSelected(supplier); setMode('return'); }}><RotateCcw size={13} /> Return</Button>
              </div>
            </div>
          ))}
          {suppliers.length === 0 && <div className="p-8 text-center text-body text-fg-tertiary">No suppliers yet.</div>}
        </div>
      </section>

      <aside className="flex min-h-0 flex-col gap-4">
        <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-heading text-fg-primary">Purchase orders</h2>
              <p className="text-label text-fg-secondary">Latest 50 entries</p>
            </div>
            <Button onClick={() => navigate('/purchases/new')}><ShoppingBag size={14} /> Receive stock</Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
          {purchaseOrders.map((po) => (
            <div key={po.id} className="border-b border-stroke-hairline/5 px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-fg-primary">{po.poNumber}</div>
                  <div className="text-label text-fg-tertiary">{supplierNames.get(po.supplierId) ?? 'Supplier'} · {po.status}</div>
                </div>
                <div className="text-right font-medium tabular-nums">{formatCurrency(po.totalAmount)}</div>
              </div>
            </div>
          ))}
          {purchaseOrders.length === 0 && <div className="p-8 text-center text-body text-fg-tertiary">No purchase orders yet.</div>}
        </div>
      </aside>

      <Dialog open={mode !== null} onOpenChange={(open) => !open && setMode(null)}>
        <DialogContent title={mode === 'payment' ? 'Record supplier payment' : mode === 'return' ? 'Create purchase return' : selected ? 'Edit supplier' : 'New supplier'} className={mode === 'return' ? 'w-[720px]' : 'w-[560px]'}>
          {mode === 'supplier' && <SupplierForm merchantId={merchantId} supplier={selected} onDone={() => setMode(null)} />}
          {mode === 'payment' && selected && <SupplierPaymentForm storeId={activeStoreId} supplier={selected} onDone={() => setMode(null)} />}
          {mode === 'return' && selected && <PurchaseReturnForm storeId={activeStoreId} userId={userId} supplier={selected} onDone={() => setMode(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SupplierForm({ merchantId, supplier, onDone }: { merchantId: string; supplier: Supplier | null; onDone: () => void }) {
  const [name, setName] = useState(supplier?.name ?? '');
  const [phone, setPhone] = useState(supplier?.phone ?? '');
  const [gstin, setGstin] = useState(supplier?.gstin ?? '');
  const [contactPerson, setContactPerson] = useState(supplier?.contactPerson ?? '');
  const [email, setEmail] = useState(supplier?.email ?? '');
  const [address, setAddress] = useState(supplier?.address ?? '');
  const [creditLimit, setCreditLimit] = useState(String(supplier?.creditLimit ?? 0));
  const [creditDays, setCreditDays] = useState(String(supplier?.creditDays ?? 0));

  async function save() {
    // Used to return silently: the button did nothing and said nothing.
    if (!name.trim()) { notifyError('Enter the supplier’s name.', 'supplier-form'); return; }
    let savedId = supplier?.id ?? '';
    let editDiff: { before: Record<string, unknown>; after: Record<string, unknown> } | null = null;
    await txn(async (db) => {
      if (supplier) {
        const before: Record<string, unknown> = {};
        const after: Record<string, unknown> = {};
        const track: [string, unknown, unknown][] = [
          ['name', supplier.name, name.trim()],
          ['phone', supplier.phone, phone.trim() || null],
          ['creditLimit', supplier.creditLimit, Number(creditLimit) || 0],
          ['creditDays', supplier.creditDays, Number(creditDays) || 0],
          ['gstin', supplier.gstin, gstin.trim().toUpperCase() || null],
        ];
        for (const [k, oldV, newV] of track) {
          if (oldV !== newV) { before[k] = oldV; after[k] = newV; }
        }
        if (Object.keys(after).length > 0) editDiff = { before, after };
        await supplier.update((record) => {
          record.name = name.trim();
          record.phone = phone.trim() || null;
          record.gstin = gstin.trim().toUpperCase() || null;
          record.contactPerson = contactPerson.trim() || null;
          record.email = email.trim() || null;
          record.address = address.trim() || null;
          record.creditLimit = Number(creditLimit) || 0;
          record.creditDays = Number(creditDays) || 0;
          record.version = supplier.version + 1;
          record.isSynced = false;
        });
      } else {
        const created = await db.get<Supplier>('suppliers').create((record) => {
          record.merchantId = merchantId;
          record.name = name.trim();
          record.contactPerson = contactPerson.trim() || null;
          record.phone = phone.trim() || null;
          record.email = email.trim() || null;
          record.gstin = gstin.trim().toUpperCase() || null;
          record.address = address.trim() || null;
          record.creditLimit = Number(creditLimit) || 0;
          record.creditDays = Number(creditDays) || 0;
          record.currentBalance = 0;
          record.bankDetails = null;
          record.tags = null;
          record.version = 1;
          record.isSynced = false;
          record.isDeleted = false;
          record.lastSyncCurrentBalance = null;
        });
        savedId = created.id;
      }
    });

    if (supplier) {
      if (editDiff) {
        const d = editDiff as { before: Record<string, unknown>; after: Record<string, unknown> };
        void logActivity({
          action: 'supplier_updated',
          entityType: 'supplier',
          entityId: savedId,
          changes: { summary: `${name.trim()} · ${Object.keys(d.after).join(', ')} changed`, before: d.before, after: d.after },
        });
      }
    } else {
      void logActivity({
        action: 'supplier_created',
        entityType: 'supplier',
        entityId: savedId,
        changes: { summary: `${name.trim()}${phone.trim() ? ` · ${phone.trim()}` : ''}` },
      });
    }
    notifySuccess(supplier ? `Supplier “${name.trim()}” updated.` : `Supplier “${name.trim()}” added.`, 'supplier-form');
    onDone();
  }
  return (
    <div className="space-y-4 p-5">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
        <Field label="Contact person"><Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} /></Field>
        <Field label="Phone"><Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} /></Field>
        <Field label="Email"><Input value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="GSTIN"><Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase().slice(0, 15))} /></Field>
        <Field label="Credit days"><Input value={creditDays} onChange={(e) => setCreditDays(e.target.value)} /></Field>
        <Field label="Credit limit"><Input value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} /></Field>
      </div>
      <Field label="Address"><Input value={address} onChange={(e) => setAddress(e.target.value)} /></Field>
      <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onDone}>Cancel</Button><Button onClick={() => void save()}>Save supplier</Button></div>
    </div>
  );
}

function SupplierPaymentForm({ storeId, supplier, onDone }: { storeId: string; supplier: Supplier; onDone: () => void }) {
  const [amount, setAmount] = useState(String(supplier.currentBalance || ''));
  const [method, setMethod] = useState('CASH');
  const [reference, setReference] = useState('');
  async function save() {
    const result = await recordSupplierPayment({ supplier, storeId, amount: Number(amount), method, reference });
    // A failure used to be silent — the form just stayed open.
    if (!result.ok) { notifyError(result.message ?? 'Could not record the payment.', 'supplier-payment'); return; }
    notifySuccess(`Payment of ${formatCurrency(Number(amount))} to ${supplier.name} recorded.`, 'supplier-payment');
    onDone();
  }
  return (
    <div className="space-y-4 p-5">
      <div className="text-body text-fg-secondary">Outstanding {formatCurrency(supplier.currentBalance)}</div>
      <Field label="Amount"><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus /></Field>
      <Field label="Method"><Input value={method} onChange={(e) => setMethod(e.target.value.toUpperCase())} /></Field>
      <Field label="Reference"><Input value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
      <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onDone}>Cancel</Button><Button onClick={() => void save()}>Record payment</Button></div>
    </div>
  );
}

function PurchaseReturnForm({ storeId, userId, supplier, onDone }: { storeId: string; userId: string; supplier: Supplier; onDone: () => void }) {
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<Array<{ product: Product; quantity: string; unitPrice: string }>>([]);
  const [reason, setReason] = useState('');
  const products = useObservable<Product[]>(
    () => search.trim().length >= 2
      ? database.get<Product>('products').query(
        Q.where('store_id', storeId),
        Q.where('is_deleted', false),
        Q.or(Q.where('name', Q.like(`%${search.trim().toLowerCase()}%`)), Q.where('sku', Q.like(`%${search.trim().toLowerCase()}%`))),
        Q.take(10),
      ).observe()
      : null,
    [search, storeId],
    [],
  );
  async function save() {
    const payload: PurchaseLine[] = lines.map((line) => ({ product: line.product, quantity: Number(line.quantity), unitPrice: Number(line.unitPrice) }));
    const result = await createPurchaseReturn({ supplier, storeId, createdById: userId, reason, lines: payload });
    if (!result.ok) { notifyError(result.message ?? 'Could not save the return.', 'purchase-return'); return; }
    notifySuccess(`Purchase return ${result.returnNumber ?? ''} saved.`.replace('  ', ' '), 'purchase-return');
    onDone();
  }
  return (
    <div className="space-y-4 p-5">
      <Field label="Add product"><Input value={search} onChange={(e) => setSearch(e.target.value)} autoFocus /></Field>
      {products.length > 0 && (
        <div className="rounded-md border border-stroke-hairline/10">
          {products.map((product) => (
            <button key={product.id} className="block w-full px-3 py-2 text-left hover:bg-bg-subtle" onClick={() => { setLines((prev) => [...prev, { product, quantity: '1', unitPrice: String(product.purchasePrice || 0) }]); setSearch(''); }}>
              {product.name}
            </button>
          ))}
        </div>
      )}
      <div className="rounded-md border border-stroke-hairline/10">
        {lines.map((line, idx) => (
          <div key={`${line.product.id}-${idx}`} className="grid grid-cols-[1fr_90px_110px] gap-2 border-b border-stroke-hairline/5 p-2">
            <div className="text-body text-fg-primary">{line.product.name}</div>
            <Input value={line.quantity} onChange={(e) => setLines((prev) => prev.map((l, i) => i === idx ? { ...l, quantity: e.target.value } : l))} />
            <Input value={line.unitPrice} onChange={(e) => setLines((prev) => prev.map((l, i) => i === idx ? { ...l, unitPrice: e.target.value } : l))} />
          </div>
        ))}
        {lines.length === 0 && <div className="p-4 text-body text-fg-tertiary">No return items.</div>}
      </div>
      <Field label="Reason"><Input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onDone}>Cancel</Button><Button onClick={() => void save()} disabled={lines.length === 0}>Create return</Button></div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="mb-1 block text-label uppercase text-fg-tertiary">{label}</span>{children}</label>;
}
